import { Controller } from "@hotwired/stimulus"

// ReactionsPickerController
// -------------------------
// Powers the dual-interaction model on any reaction button:
//   • Short tap (< 250ms)          → submits the hidden "trigger" button.
//                                    The button itself posts/deletes :like kind
//                                    (see shared/_reactions_button.html.erb).
//   • Long press  (>= 300ms)       → opens the reactions/_picker popup with
//                                    the 6 available kinds. Picker closes on
//                                    Escape / outside-click / scroll.
//
// Coexistence with swipe_reply_controller & context_menu_controller
// -----------------------------------------------------------------
// Messages and comments already have long-press bindings via swipe_reply_controller
// (opens the context menu) and swipe reply. To avoid conflicts:
//   • When the reactions button is attached inside a message/comment bubble,
//     we still get first pointerdown here, BUT we listen to the same
//     "context-menu:open" event to stack the picker ON TOP (higher z-index
//     than the context menu), instead of fighting for the gesture.
//   • Escape / outside-click closes the picker first, then the context menu.
//
// Values:
//   reactableType — "User" | "Post" | ... (mirrored on the wrapping <div>)
//   reactableId   — UUID
//   currentKind   — kind already submitted by current_user (to highlight)
//   longPressMs   — long press threshold (default 300ms per spec)
//
// Targets:
//   trigger — the short-tap <form>'s submit button (button_to)
//   panel   — the picker popup
//   option  — individual kind buttons inside the picker
export default class extends Controller {
  static targets = ["trigger", "panel", "option", "overlay"]
  static values = {
    reactableType: String,
    reactableId: String,
    currentKind: String,
    longPressMs: { type: Number, default: 300 },
    tapMaxMs: { type: Number, default: 250 }
  }

  connect() {
    this._pressTimer = null
    this._pressStartedAt = 0
    this._opened = false
    this._longPressed = false

    this._onDocClick = this._handleDocClick.bind(this)
    this._onKeydown = this._handleKeydown.bind(this)
    this._onScroll = this._handleScroll.bind(this)
    this._onContextMenuOpen = this._handleContextMenuOpen.bind(this)

    document.addEventListener("context-menu:open", this._onContextMenuOpen)
  }

  disconnect() {
    this._clearTimer()
    this._detachDismissListeners()
    document.removeEventListener("context-menu:open", this._onContextMenuOpen)
  }

  // ----------------------------------------------------------------
  // Pointer handlers on the "trigger" (short-tap) button.
  // ----------------------------------------------------------------

  pressStart(event) {
    // Left button / primary touch only
    if (event.button !== undefined && event.button !== 0) return

    this._longPressed = false
    this._pressStartedAt = performance.now()

    this._clearTimer()
    this._pressTimer = setTimeout(() => {
      this._longPressed = true
      this._vibrate()
      this.open()
    }, this.longPressMsValue)
  }

  pressEnd(event) {
    this._clearTimer()
    if (this._longPressed) {
      // Prevent the button_to from submitting (we're in picker-mode now).
      event.preventDefault()
      event.stopPropagation()
      this._longPressed = false

    }
    // Short tap: allow the button_to to submit normally (no-op here).
  }

  cancelPress() {
    this._clearTimer()
  }

  preventContext(event) {
    // Suppress the native context menu on long-press (mobile/right-click).
    event.preventDefault()
  }

  // ----------------------------------------------------------------
  // Popup lifecycle
  // ----------------------------------------------------------------

  open() {
    if (!this.hasPanelTarget) return
    if (this._opened) return

    this._opened = true
    const panel = this.panelTarget

    // Position above the trigger (defaults to bottom center of trigger).
    this._positionPanel(panel)

    panel.classList.remove("opacity-0", "scale-95", "pointer-events-none")
    panel.classList.add("opacity-100", "scale-100")

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("opacity-0", "pointer-events-none")
      this.overlayTarget.classList.add("opacity-100")
    }

    this._attachDismissListeners()
  }

  close() {
    if (!this._opened) return
    this._opened = false
    if (!this.hasPanelTarget) return

    const panel = this.panelTarget
    panel.classList.remove("opacity-100", "scale-100")
    panel.classList.add("opacity-0", "scale-95", "pointer-events-none")

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("opacity-100")
      this.overlayTarget.classList.add("opacity-0", "pointer-events-none")
    }

    this._detachDismissListeners()
  }

  // Close cascade fires when a swipe_reply_controller fires "context-menu:open":
  // we stack the picker over the context menu, but we also accept the host
  // page dismissing both — the context menu owner will close independently.
  _handleContextMenuOpen(event) {
    const detail = event.detail || {}
    const matches =
      detail.reactableType === this.reactableTypeValue &&
      String(detail.reactableId) === String(this.reactableIdValue)
    if (matches) {
      this.open()
    }
  }

  _handleDocClick(event) {
    if (!this._opened) return
    if (this.element.contains(event.target)) return
    this.close()
  }

  _handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  _handleScroll() {
    this.close()
  }

  _attachDismissListeners() {
    document.addEventListener("click", this._onDocClick, true)
    document.addEventListener("keydown", this._onKeydown)
    window.addEventListener("scroll", this._onScroll, true)
  }

  _detachDismissListeners() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("scroll", this._onScroll, true)
  }

  _positionPanel(panel) {
    // Use position: fixed (set via CSS) so we escape any overflow:hidden
    // ancestors (feed cards, modals, etc.). We anchor the picker ABOVE the
    // trigger and clamp horizontally to stay fully visible in the viewport.
    const GAP = 10
    const MARGIN = 8

    const triggerRect = this.element.getBoundingClientRect()
    const viewportW = window.innerWidth || document.documentElement.clientWidth

    // Measure the panel (briefly make it visible-but-transparent to read size).
    const prevVisibility = panel.style.visibility
    panel.style.visibility = "hidden"
    panel.style.top = "0px"
    panel.style.left = "0px"
    panel.style.transform = "none"
    const panelW = panel.offsetWidth
    const panelH = panel.offsetHeight
    panel.style.visibility = prevVisibility

    const triggerCenterX = triggerRect.left + triggerRect.width / 2
    let left = triggerCenterX - panelW / 2

    // Clamp horizontally so the picker is always fully visible.
    if (left < MARGIN) left = MARGIN
    if (left + panelW > viewportW - MARGIN) left = viewportW - MARGIN - panelW

    // Always above the trigger, unless there's literally no room upward
    // (rare — fallback to below to avoid hiding the picker off-screen).
    let top = triggerRect.top - panelH - GAP
    if (top < MARGIN) {
      top = triggerRect.bottom + GAP
    }

    panel.style.left = `${Math.round(left)}px`
    panel.style.top = `${Math.round(top)}px`
    panel.style.transform = "" // reset — scale-95/100 via Tailwind classes
  }

  _clearTimer() {
    if (this._pressTimer) {
      clearTimeout(this._pressTimer)
      this._pressTimer = null
    }
  }

  _vibrate() {
    if (navigator.vibrate) navigator.vibrate(15)
  }
}
